import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AuthService, BaseDataSource } from '../../services';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { CustomFilter } from '../../models';
import { FormEvent, TableAction, TableColumn, TableFilter } from '../../types/types';
import { fromEvent, debounceTime, distinctUntilChanged, tap, merge, Observable } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { pageSizeDefault, sortDescending } from '../../constants/constants';
import { SubSink } from 'subsink';

@Component({
  selector: 'mmg-base-list',
  templateUrl: './base-list.component.html',
  styleUrls: ['./base-list.component.scss']
})
export class BaseListComponent<T> implements AfterViewInit, OnInit, OnDestroy {

  @Input() columns: TableColumn[] = [];
  @Input() actions: TableAction[] = [];
  @Input() filters: TableFilter[] = [];
  @Input() dataSource!: BaseDataSource<T>;
  @Input() reload?: Observable<boolean>;
  @Input() lazyLoad: boolean = false;
  @Input() customFilter?: CustomFilter;
  @Input() filterData: any;

  @Output() action = new EventEmitter<{ event: FormEvent, data?: T | any, filter?: any }>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('search') search!: ElementRef;

  private readonly subs = new SubSink();

  readonly contentTypes = "text/csv";
  readonly pageSize = pageSizeDefault;

  readonly breakpoint$: Observable<BreakpointState>;

  currentBreakpoint = '';
  isMobile = false;

  headers: string[] = [];
  availableColumns: TableColumn[] = [];

  buttons: Partial<{
    [name: string]: TableAction | undefined,
    add: TableAction | undefined,
    export: TableAction | undefined,
    import: TableAction | undefined
  }> = {};

  listActions: TableAction[] = [];

  constructor(
    private readonly breakpoint: BreakpointObserver,
    private readonly authService: AuthService
  ) {
    this.breakpoint$ = this.breakpoint
      .observe([
        Breakpoints.Large,
        Breakpoints.Medium,
        Breakpoints.Small,
        Breakpoints.XSmall])
      .pipe(distinctUntilChanged());
  }

  get textFilter(): any {
    return this.search?.nativeElement;
  } 

  get selectFilters(): TableFilter[] {
    return this.filters.filter(f => f.type === 'select');
  };

  ngOnInit(): void {
    this.actions.forEach(action => {
      if (action.type === 'button') {
        this.buttons[action.event] = action;
      } else if (action.type === 'list') {
        const allowedAction = !action.roles?.length
          || action.roles.some(r => this.authService.checkRole(r));
        if (allowedAction) {
          this.listActions.push(action);  
        }
      }
    });

    if (!this.lazyLoad) {
      this.dataSource.load();
    }

    if (!!this.reload) {
      this.subs.sink = this.reload
        .subscribe(status => {
          if (status) this.load();
        });
    }

    this.subs.sink = this.breakpoint$
      .subscribe(() => {
        this.isMobile = this.breakpoint.isMatched(Breakpoints.Small) ||
          this.breakpoint.isMatched(Breakpoints.XSmall);

        if (this.isMobile) {
          this.availableColumns = [...this.columns.filter(c => !!c.mobile)];
          this.headers = this.availableColumns.map(c => c.name);
        } else {
          this.availableColumns = [...this.columns];
          this.headers = this.availableColumns.map(c => c.name);
        }
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (!!this.filters?.length) {
      this.subs.sink = fromEvent(this.textFilter, 'keyup')
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            this.paginator.pageIndex = 0;
            this.load();
          }))
        .subscribe();
    }
    const subs = [
      this.sort.sortChange
        .subscribe(() => this.paginator.pageIndex = 0),
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.load()))
        .subscribe()
    ];

    this.subs.add(...subs);
  }

  load(): void {
    const filter = this.buildFilter();
    this.dataSource.load(
      filter,
      this.sort.active,
      this.sort.direction === sortDescending,
      this.paginator.pageIndex,
      this.paginator.pageSize);
  }

  clear(): void {
    this.textFilter.value = '';
    this.load();
  }

  do(event: FormEvent, data?: T | any): void {
    const filter = this.dataSource.baseFilter;
    this.action.emit({ event, data, filter });
  }

  private buildFilter(): any {
    if (!!this.filterData) return this.filterData;

    const condition: any = {};

    this.filters.forEach(filter => {
      const value: string = filter.type === 'text'
        ? this.textFilter.value ?? ''
        : this.selectFilters.find(selectedFilter =>
          selectedFilter.property == filter.property)?.value ?? '';

      if (value) condition[filter.property] = value;
    });

    return condition;
  }

}